/**
 * Pattern Recognition for Won/Lost Deals
 * 
 * Simple rules-based pattern detection:
 * - What worked in wins
 * - What went wrong in losses
 * - Connect patterns to active deals
 */

import { Deal } from '@/hooks/useDeals';
import { Lead } from '@/hooks/useLeads';
import { differenceInDays, differenceInHours } from 'date-fns';

export interface WinPattern {
  type: 'value_threshold' | 'follow_up_timing' | 'touchpoints' | 'channel';
  description: string;
  count: number;
  avgDays?: number;
}

export interface LossPattern {
  type: 'inactivity' | 'reason' | 'stage';
  description: string;
  count: number;
  riskDeals: Deal[];
}

/**
 * Detect patterns in won deals
 */
export function detectWinPatterns(wonDeals: Deal[], leads: Lead[]): WinPattern[] {
  const patterns: WinPattern[] = [];
  
  if (wonDeals.length === 0) return patterns;
  
  // Pattern 1: High-value deals closed faster
  const highValueThreshold = 3000;
  const highValueDeals = wonDeals.filter(d => (d.value || 0) >= highValueThreshold);
  if (highValueDeals.length >= 2) {
    const avgDays = highValueDeals
      .map(d => {
        const closedAt = (d as any).closed_at ? new Date((d as any).closed_at) : new Date(d.updated_at);
        const createdAt = new Date(d.created_at);
        return differenceInDays(closedAt, createdAt);
      })
      .filter(d => d >= 0);
    
    if (avgDays.length > 0) {
      const avg = Math.round(avgDays.reduce((sum, d) => sum + d, 0) / avgDays.length);
      patterns.push({
        type: 'value_threshold',
        description: `Deals above ${highValueThreshold.toLocaleString()}€ closed in ${avg} days on average`,
        count: highValueDeals.length,
        avgDays: avg,
      });
    }
  }
  
  // Pattern 2: Follow-up timing (if we have action_events data, we'd check here)
  // For now, we'll use a simple heuristic based on win_reason
  const followUpWins = wonDeals.filter(d => (d as any).win_reason === 'follow_up');
  if (followUpWins.length >= 2) {
    patterns.push({
      type: 'follow_up_timing',
      description: `${followUpWins.length} deals closed after consistent follow-up`,
      count: followUpWins.length,
    });
  }
  
  // Pattern 3: Touchpoints (simplified - would need action_events)
  // Pattern 4: Channel (WhatsApp vs Call) - would need action_events
  
  return patterns;
}

/**
 * Find active deals with same risk pattern as lost deals
 */
export function findSimilarRiskDeals(
  lostDeal: Deal,
  lostLead: Lead | undefined,
  activeDeals: Deal[],
  activeLeads: Lead[]
): Deal[] {
  const similar: Deal[] = [];
  const lostReason = (lostDeal as any).lost_reason;
  const lostValue = lostDeal.value || 0;
  
  // Pattern 1: Same loss reason
  if (lostReason) {
    activeDeals.forEach(deal => {
      const lead = activeLeads.find(l => l.id === deal.lead_id);
      if (!lead) return;
      
      // Check if deal is at similar stage and has similar characteristics
      const dealStage = deal.stage;
      if (['proposal_sent', 'negotiation'].includes(dealStage || '')) {
        // Check inactivity
        const lastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
        const now = new Date();
        
        if (lostReason === 'time' || lostReason === 'ghosted') {
          // Check if inactive
          if (!lastContact || differenceInHours(now, lastContact) > 72) {
            similar.push(deal);
          }
        } else if (lostReason === 'price' && (deal.value || 0) > 0) {
          // Similar value range
          const valueDiff = Math.abs((deal.value || 0) - lostValue);
          if (valueDiff < lostValue * 0.5) {
            similar.push(deal);
          }
        }
      }
    });
  }
  
  // Pattern 2: Same inactivity pattern
  if (lostLead) {
    const lastContact = lostLead.last_interaction_at ? new Date(lostLead.last_interaction_at) : null;
    const lostAt = (lostDeal as any).lost_at ? new Date((lostDeal as any).lost_at) : new Date(lostDeal.updated_at);
    
    if (lastContact) {
      const daysInactive = differenceInDays(lostAt, lastContact);
      
      activeDeals.forEach(deal => {
        const lead = activeLeads.find(l => l.id === deal.lead_id);
        if (!lead) return;
        
        const activeLastContact = lead.last_interaction_at ? new Date(lead.last_interaction_at) : null;
        if (activeLastContact) {
          const activeDaysInactive = differenceInDays(new Date(), activeLastContact);
          // If active deal is approaching same inactivity threshold
          if (activeDaysInactive >= daysInactive * 0.7 && activeDaysInactive < daysInactive) {
            similar.push(deal);
          }
        }
      });
    }
  }
  
  return similar;
}

